import React, { useEffect, useMemo, useState } from "react";
import CustomDropdown from "../../../../components/customDropDown/CustomDropdown";
import { timeDataSource, timespentDataSource } from "../../data";
import "./TimeSpentPanel.scss";
import "../../../../components/typographyText/styles.scss";
import CustomSlider from "../../../../components/customSlider/CustomSlider";
import { ToastDisplayer } from "../../../../components/toastDisplay/ToastDisplayer";
import { Button, DateBox, LoadPanel, TextArea } from "devextreme-react";
import CustomButton from "../../../../components/customButton/CustomButton";
import {
  deleteTimeSpent,
  getTimeSpentById,
  saveTimeSpent,
  updateTimeSpent,
} from "../../../../api/ticketApi";
import { useAuth } from "../../../../contexts/auth";
import CustomEmpty from "../../../../components/customEmptyView/CustomEmpty";
import { filterTimeSpentApi } from "../../../../api/filterAPI";

export default function TimeSpentPanel({
  detailTicketData = [],
  getTicketDetails,
  isTicketClosed,
}) {
  const { user } = useAuth();
  const [isTsSliderOpen, setTsSliderOpen] = useState(false);
  const [isTsSlidingOut, setTsIsSlidingOut] = useState(false);

  const [isEditSliderOpen, setEditSliderOpen] = useState(false);
  const [isEditSlidingOut, setIsEditSlidingOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editTsByIdData, setEditTsByIdData] = useState();
  const [timeFilter, setTimeFilter] = useState(1); //"All Time" = 1
  const today = new Date();

  const [filteredTimeSpent, setFilteredTimeSpent] = useState([]);

  const getTimeFilterKey = (value) => {
    switch (value) {
      case 2:
        return "today";
      case 3:
        return "yesterday";
      case 4:
        return "this_week";
      case 5:
        return "this_month";
      case 6:
        return "past";
      default:
        return "all";
    }
  };

  const fetchFilteredTimeSpent = async (timeFilterKey) => {
    try {
      setLoading(true);

      const res = await filterTimeSpentApi({
        ticketcode: detailTicketData?.ticket?.ticketcode,
        filter: timeFilterKey,
      });

      if (res.success) {
        const formattedData = (res.data.timespent || []).map((item) => ({
          ...item,

          //  format start_date
          formattedDate: formatDate(item.start_date),

          //  format update_date
          update_date: formatDate(item.update_date),

          //  fallback safety
          activitydoneby: item.activitydoneby || "Unknown",
          updated_by: item.updated_by || "N/A",
        }));

        setFilteredTimeSpent(formattedData);
      } else {
        ToastDisplayer("error", res.message);
      }
    } catch (err) {
      ToastDisplayer("error", "Failed to fetch time spent");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (detailTicketData?.ticket?.ticketcode) {
      fetchFilteredTimeSpent("all");
    }
  }, [detailTicketData]);

  //////////////////////////////////////////////// add time spent functions
  const [timeSpentData, setTimeSpentData] = useState({
    startDateTime: null,
    endDateTime: null,
    description: "",
  });

  const handleInputChange = (field, value) => {
    setTimeSpentData((prev) => {
      if (field === "startDateTime") {
        return {
          ...prev,
          startDateTime: value,
          endDateTime:
            prev.endDateTime && prev.endDateTime < value
              ? value
              : prev.endDateTime,
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleAddTimeSpent = () => {
    const createdDateTime = getTicketCreatedDateTime();
    const now = new Date();

    const defaultStart =
      createdDateTime && createdDateTime < now ? now : createdDateTime;

    setTimeSpentData({
      startDateTime: defaultStart,
      endDateTime: now,
      description: "",
    });

    setTsSliderOpen(true);
  };

  const handleTsCloseSlider = () => {
    setTsIsSlidingOut(true);
    resetTimeSpentData();
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, "0");

    return `${hours}:${minutes}:${seconds} ${ampm}`;
  };

  function convertTo12HourFormat(time) {
    const [hour, minute, second] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12; // Convert 0 hour to 12 for AM
    return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
  }

  function dateFormat(sdate) {
    const date = new Date(sdate);
    const day = String(date.getDate()).padStart(2, "0"); // Ensure two digits
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const validateTimeSpentData = (data) => {
    if (!data.startDateTime) {
      ToastDisplayer("error", "Start DateTime is required");
      return false;
    }

    if (!data.endDateTime) {
      ToastDisplayer("error", "End Date and Time is required");
      return false;
    }

    // NEW VALIDATION
    const createdDateTime = getTicketCreatedDateTime();

    if (createdDateTime && data.startDateTime < createdDateTime) {
      ToastDisplayer(
        "error",
        "Start time cannot be before ticket created date and time",
      );
      return false;
    }

    if (data.endDateTime < data.startDateTime) {
      ToastDisplayer(
        "error",
        "End Date and Time cannot be before Start Date and Start Time",
      );
      return false;
    }

    const now = new Date();

    if (data.endDateTime > now) {
      ToastDisplayer("error", "End time cannot be greater than current time");
      return false;
    }

    return true;
  };

  const handleTsSaveDetails = async () => {
    if (!validateTimeSpentData(timeSpentData)) {
      return;
    }
    if (!timeSpentData?.description) {
      return ToastDisplayer(
        "error",
        "Please add description of the time spent before saving",
      );
    }
    try {
      setLoading(true);

      const startDate = new Date(timeSpentData.startDateTime);
      const endDate = new Date(timeSpentData.endDateTime);

      const starttime = formatTime(startDate);
      const endtime = formatTime(endDate);

      const start_date = dateFormat(startDate);
      const end_date = dateFormat(endDate);
      const modifiedTsData = {
        starttime: starttime,
        endtime: endtime,
        description: timeSpentData.description,
        start_date: start_date,
        end_date: end_date,
        ticketcode: detailTicketData.ticket.ticketcode,
        usertransid:
          user?.userrole === "companyuser" ? user?.usertransid : null,
        companytransid:
          user?.userrole === "company" ? user?.companytransid : null,
      };

      const result = await saveTimeSpent(modifiedTsData);
      if (result.status === 201 && result.statusText === "Created") {
        ToastDisplayer("success", "Time Spent saved successfully");
        setTsSliderOpen(false);
        setTsIsSlidingOut(false);
        getTicketDetails();
      }
    } catch (error) {
      ToastDisplayer(
        "error",
        error.response?.data?.detail || "Failed to save Time Spent",
      );
    } finally {
      setLoading(false);
      setTimeSpentData({
        starttime: null,
        endtime: null,
        description: "",
        startDate: "",
      });
    }
  };

  /////////////////////////////////////////////// edit time spent functions
  const [edittimeSpentData, setEditTimeSpentData] = useState({
    startDateTime: null,
    endDateTime: null,
    description: "",
  });

  const mergeDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;

    const [day, month, year] = dateStr.split("-").map(Number);
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds);
  };

  const handleEditInputChange = (field, value) => {
    if (field === "starttime" || field === "endtime") {
      let newTime = value;

      // Convert string ("HH:mm") to a Date object
      if (typeof value === "string") {
        const [hours, minutes] = value.split(":").map(Number);
        newTime = new Date();
        newTime.setHours(hours, minutes, 0, 0);
      }

      setEditTimeSpentData((prevData) => ({
        ...prevData,
        [field]: newTime,
      }));
    } else {
      setEditTimeSpentData((prevData) => ({
        ...prevData,
        [field]: value,
      }));
    }
  };

  function formatEditTime(inputTime) {
    const date = new Date(inputTime);
    if (isNaN(date.getTime())) {
      return "Invalid date format";
    }
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedTime = `${hours
      .toString()
      .padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
    return formattedTime;
  }

  const handleEditTimeSpent = async (tid) => {
    try {
      setLoading(true);

      const result = await getTimeSpentById(tid);

      if (result.status === 200) {
        const rsData = result.data.data;

        const startDateTime = mergeDateTime(
          rsData.start_date,
          rsData.starttime,
        );

        const endDateTime = mergeDateTime(rsData.end_date, rsData.endtime);

        setEditSliderOpen(true);

        setEditTimeSpentData({
          ...rsData,
          startDateTime,
          endDateTime,
        });
      }
    } catch (error) {
      ToastDisplayer(
        "error",
        error?.response?.data?.message || "Unexpected error occurred!",
      );
    } finally {
      setLoading(false);
    }
  };

  const convertToTime = (timeStr) => {
    if (!timeStr) return new Date();
    const [hours, minutes] = timeStr.split(":").map(Number);
    const time = new Date();
    time.setHours(hours, minutes, 0, 0);
    return time;
  };

  const handleEditCloseSlider = () => {
    setEditTimeSpentData({
      startDateTime: null,
      endDateTime: null,
      description: "",
    });

    setIsEditSlidingOut(true);
  };

  const handleEditDetails = async () => {
    if (!validateTimeSpentData(edittimeSpentData)) {
      return;
    }

    try {
      setLoading(true);

      const startDate = new Date(edittimeSpentData.startDateTime);
      const endDate = new Date(edittimeSpentData.endDateTime);

      const starttime = formatTime(startDate);
      const endtime = formatTime(endDate);

      const start_date = dateFormat(startDate);
      const end_date = dateFormat(endDate);

      const modifiedTsData = {
        ticketcode: detailTicketData?.ticket?.ticketcode,

        usertransid:
          user?.userrole === "companyuser" ? user?.usertransid : null,

        companytransid:
          user?.userrole === "company" ? user?.companytransid : null,

        starttime,
        endtime,
        start_date,
        end_date,

        description: edittimeSpentData.description,
      };

      const result = await updateTimeSpent(
        modifiedTsData,
        edittimeSpentData.transid,
      );

      if (result.status === 200) {
        getTicketDetails();
        ToastDisplayer("success", result?.data?.message);
        setIsEditSlidingOut(true);
      }
    } catch (error) {
      ToastDisplayer(
        "error",
        error?.response?.data?.detail || "Unable to update this record!",
      );
    } finally {
      setLoading(false);
    }
  };

  function convertTo12HourFormatEdit(time) {
    if (!time) return "";
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const convertedHours = hours % 12 || 12;
    return `${convertedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  /////////////////////////////////////////////////
  /// komal code
  const handleTsDelete = async (ticketcode) => {
    try {
      setLoading(true);
      const result = await deleteTimeSpent(ticketcode);
      if (result) {
        ToastDisplayer("success", result?.message);
        getTicketDetails();
      }
    } catch (error) {
      ToastDisplayer(
        "error",
        error.response?.data?.errors?.non_field_errors || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  //Function to formate Date sent from Backend
  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    // Handle DD-MM-YYYY format
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 2) {
      const [day, month, year] = dateStr.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-");
    }

    // Handle ISO format (update_date etc.)
    const date = new Date(dateStr);

    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  const groupTimeSpent = (timeSpent) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const startOfWeek = new Date();
    startOfWeek.setDate(today.getDate() - today.getDay());

    const grouped = {
      today: [],
      yesterday: [],
      thisWeek: [],
      past: [],
    };

    timeSpent?.forEach((time) => {
      const timeDate = new Date(time.start_date);
      time.formattedDate = formatDate(time.start_date);
      time.update_date = formatDate(time.update_date);
      if (timeDate.toDateString() === today.toDateString()) {
        grouped.today.unshift(time);
      } else if (timeDate.toDateString() === yesterday.toDateString()) {
        grouped.yesterday.unshift(time);
      } else if (timeDate >= startOfWeek) {
        grouped.thisWeek.unshift(time);
      } else {
        grouped.past.unshift(time);
      }
    });

    return grouped;
  };

  const groupedTimeSpent = useMemo(
    () => groupTimeSpent(detailTicketData.timespent),
    [detailTicketData.timespent],
  );

  const renderTimeSpentGroup = (groupTitle, timeSpent) =>
    timeSpent.length > 0 && (
      <div className="actMain">
        <div className="actHead detail-small-bold">{groupTitle}</div>
        {timeSpent.map((time, index) => (
          <div className="actInfo" key={time.transid}>
            <div className="tsLeft">
              <div className="actIndex detail-mid-light">{index + 1}.</div>

              <div className="actInnerInfo">
                <div className="actDesc detail-mid-light">
                  {time.description}
                </div>

                <div className="actTime italic-small-bold">
                  Start: {convertTo12HourFormat(time.starttime)} ,{" "}
                  {time?.start_date} - End:{" "}
                  {convertTo12HourFormat(time.endtime)} , {time?.end_date}
                </div>
                <div className="actDateDetail popup-sub-text">
                  Created on {time.formattedDate}, By {time.activitydoneby}
                  <span className="diffDiv">|</span>
                  {/* </div>
                <div className="actEditDetail italic-small-bold"> */}
                  Edited on {time.update_date}, By {time.updated_by}
                </div>
              </div>
            </div>
            {!isTicketClosed && user && user?.userrole !== "customer" && (
              <div className="tsMethod">
                <div
                  className="tsEdit"
                  onClick={() => handleEditTimeSpent(time.transid)}
                >
                  <i className="ri-pencil-fill"></i>
                </div>
                <div
                  className="tsDelete"
                  onClick={() => handleTsDelete(time.transid)}
                >
                  <i className="ri-delete-bin-fill"></i>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  ///////////////

  // rishab code
  ///////////////////////////////////Handle Filter Change
  const handleFilterChange = (item) => {
    const selected = item.value;
    setTimeFilter(selected);

    const timeKey = getTimeFilterKey(selected);

    fetchFilteredTimeSpent(timeKey); //  API CALL
  };
  const renderFilteredTspnt = () => {
    const timeFilteringLogic = (timeFilter, groupedTimeSpent) => {
      //This Month filtering
      const thisMonthTimeSpent =
        detailTicketData.timespent?.filter((entry) => {
          const entryDate = new Date(entry.startDate);
          const now = new Date();
          return (
            entryDate.getMonth() === now.getMonth() &&
            entryDate.getFullYear() === now.getFullYear()
          );
        }) || [];
      switch (timeFilter) {
        case 2:
          return groupedTimeSpent.today.length === 0
            ? "Nil"
            : renderTimeSpentGroup("Today", groupedTimeSpent.today);

        case 3:
          return groupedTimeSpent.yesterday.length === 0
            ? "Nil"
            : renderTimeSpentGroup("Yesterday", groupedTimeSpent.yesterday);

        case 4:
          return groupedTimeSpent.thisWeek.length === 0
            ? "Nil"
            : renderTimeSpentGroup("This Week", groupedTimeSpent.thisWeek);
        case 4:
          return groupedTimeSpent.thisWeek.length === 0
            ? "Nil"
            : renderTimeSpentGroup("This Week", groupedTimeSpent.thisWeek);

        case 5:
          return thisMonthTimeSpent.length === 0
            ? "Nil"
            : renderTimeSpentGroup("This Month", thisMonthTimeSpent);

        case 6:
          return groupedTimeSpent.past.length === 0
            ? "Nil"
            : renderTimeSpentGroup("Past Time", groupedTimeSpent.past);
        case 6:
          return groupedTimeSpent.past.length === 0
            ? "Nil"
            : renderTimeSpentGroup("Past Time", groupedTimeSpent.past);

        case 1:
        default:
          return (
            <>
              {groupedTimeSpent.today.length > 0 &&
                renderTimeSpentGroup("Today", groupedTimeSpent.today)}
              {groupedTimeSpent.yesterday.length > 0 &&
                renderTimeSpentGroup("Yesterday", groupedTimeSpent.yesterday)}
              {groupedTimeSpent.thisWeek.length > 0 &&
                renderTimeSpentGroup("This Week", groupedTimeSpent.thisWeek)}
              {groupedTimeSpent.past.length > 0 &&
                renderTimeSpentGroup("Past Time", groupedTimeSpent.past)}
            </>
          );
      }
    };

    return timeFilteringLogic(timeFilter, groupedTimeSpent);
  };

  const resetTimeSpentData = () => {
    const createdDateTime = getTicketCreatedDateTime();
    const now = new Date();

    const defaultStart =
      createdDateTime && createdDateTime < now ? now : createdDateTime;

    setTimeSpentData({
      startDateTime: defaultStart,
      endDateTime: now,
      description: "",
    });
  };

  const convertToDate = (dateStr) => {
    if (!dateStr) return null;

    if (dateStr instanceof Date) {
      return dateStr; //  If already a Date object, return it
    }

    if (typeof dateStr === "string") {
      const [day, month, year] = dateStr.split("-").map(Number);
      return new Date(year, month - 1, day); // Month is zero-based in JS
    }

    return null;
  };

  const getTicketCreatedDateTime = () => {
    const date = detailTicketData?.ticket?.ticket_created_date;
    const time = detailTicketData?.ticket?.ticket_created_time;

    if (!date || !time) return null;

    const [day, month, year] = date.split("-").map(Number);
    const [hours, minutes, seconds] = time.split(":").map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds);
  };

  return (
    <>
      {loading && <LoadPanel visible={true} />}
      <div className="timeSpentSec">
        <div className="queryMain ticket-card">
          <div className="ticket-subject">
            <b>{`${detailTicketData?.ticket?.ticketsubject || ""}`}</b>
          </div>
          {/* <div className="queryInner">
            <div className="description-wrapper">
              <div
                className="description-content"
                dangerouslySetInnerHTML={{
                  __html: detailTicketData?.ticket?.ticketdescription || "",
                }}
              />
            </div>
          </div> */}
        </div>
        <div className="timeSpentHead">
          <div className="timeSpentLeft">
            <CustomDropdown
              myDataSource={timespentDataSource}
              placeholder="All Time"
              setSelectedValue={handleFilterChange}
              width={200}
              value={timeFilter}
            />
          </div>

          {!isTicketClosed &&
            user &&
            user.userrole !== "customer" &&
            user.userrole !== "customercontact" && (
              <div className="timeSpentRight">
                <div className="addtimeSpent" onClick={handleAddTimeSpent}>
                  <i className="ri-add-box-fill"></i>
                  <span className="detail-small-bold">Add Time Spent</span>
                </div>
              </div>
            )}
        </div>
        {/* <div className="timeSpentBody">{renderFilteredTspnt()}</div> */}
        <div className="timeSpentBody">
          {filteredTimeSpent.length === 0 ? (
            <CustomEmpty moduleName="Time" />
          ) : (
            renderTimeSpentGroup("Time Spent", filteredTimeSpent)
          )}
        </div>
      </div>

      {/* ///// add time spent */}
      <CustomSlider
        isOpen={isTsSliderOpen}
        isSlidingOut={isTsSlidingOut}
        onClose={handleTsCloseSlider}
        headtext={"Add Time Spent"}
        setSliderOpen={setTsSliderOpen}
        setIsSlidingOut={setTsIsSlidingOut}
      >
        <div className="notesMainDrawer">
          <div className="noteDrawerBody">
            <div className="noteHeadDrawer typ-sub-text">All Details</div>
            <div className="timeDrawerDesc">
              <div className="MyTextBox">
                <label className="TextBoxLabel">
                  Start Date and Time <span className="requiredClass">*</span>
                </label>
                <DateBox
                  type="datetime"
                  placeholder="Select Date and Time"
                  width="100%"
                  value={timeSpentData.startDateTime}
                  pickerType="calendar"
                  stylingMode="outlined"
                  min={getTicketCreatedDateTime()} // THIS LINE
                  max={today}
                  displayFormat="dd-MM-yyyy HH:mm"
                  onValueChanged={(e) => {
                    // console.log("e : ", e.value);
                    handleInputChange("startDateTime", e.value);
                  }}
                />
              </div>
              <div className="MyTextBox">
                <label className="TextBoxLabel">
                  End Date and Time <span className="requiredClass">*</span>
                </label>
                <DateBox
                  type="datetime"
                  placeholder="Select Date and Time"
                  width="100%"
                  value={timeSpentData.endDateTime}
                  pickerType="calendar"
                  stylingMode="outlined"
                  min={timeSpentData.startDateTime}
                  max={today}
                  displayFormat="dd-MM-yyyy HH:mm"
                  onValueChanged={(e) => {
                    // console.log("e : ", e.value);
                    handleInputChange("endDateTime", e.value);
                  }}
                />
              </div>
            </div>
            <div className="notesItem">
              <label className="noteDrawerLabel">
                Description <span className="requiredClass">*</span>
              </label>
              <TextArea
                stylingMode="outlined"
                placeholder="Add Description"
                height={"200"}
                value={timeSpentData.description}
                onValueChanged={(e) =>
                  handleInputChange("description", e.value)
                }
              />
            </div>
          </div>

          <div className="notesFootDrawer">
            <div className="btnSection">
              <div className="cancelSection">
                <Button
                  text="Cancel"
                  height={"40px"}
                  onClick={handleTsCloseSlider}
                />
              </div>

              <div className="create-button">
                <CustomButton
                  text={"Save Details"}
                  onClick={handleTsSaveDetails}
                />
              </div>
            </div>
          </div>
        </div>
      </CustomSlider>

      {/* ///// edit time spent */}
      <CustomSlider
        isOpen={isEditSliderOpen}
        isSlidingOut={isEditSlidingOut}
        onClose={handleEditCloseSlider}
        headtext={"Edit Time Spent"}
        setSliderOpen={setEditSliderOpen}
        setIsSlidingOut={setIsEditSlidingOut}
      >
        <div className="notesMainDrawer">
          <div className="noteDrawerBody">
            <div className="noteHeadDrawer typ-sub-text">All Details</div>
            <div className="timeDrawerDesc">
              <div className="MyTextBox">
                <label className="TextBoxLabel">
                  Start Date and Time <span className="requiredClass">*</span>
                </label>

                <div className="TextBoxWrapper">
                  <DateBox
                    type="datetime"
                    value={edittimeSpentData.startDateTime}
                    stylingMode="outlined"
                    width="100%"
                    displayFormat="dd-MM-yyyy HH:mm"
                    max={today}
                    onValueChanged={(e) =>
                      setEditTimeSpentData((prev) => ({
                        ...prev,
                        startDateTime: e.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="MyTextBox">
                <label className="TextBoxLabel">
                  End Date and Time <span className="requiredClass">*</span>
                </label>
                <div className="TextBoxWrapper">
                  <DateBox
                    type="datetime"
                    value={edittimeSpentData.endDateTime}
                    stylingMode="outlined"
                    width="100%"
                    displayFormat="dd-MM-yyyy HH:mm"
                    min={edittimeSpentData.startDateTime}
                    max={today}
                    onValueChanged={(e) =>
                      setEditTimeSpentData((prev) => ({
                        ...prev,
                        endDateTime: e.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="notesItem">
              <label className="noteDrawerLabel">
                Description <span className="requiredClass">*</span>
              </label>
              <TextArea
                stylingMode="outlined"
                // placeholder="Add Description"
                height={"200"}
                value={
                  edittimeSpentData?.description !== ""
                    ? edittimeSpentData?.description
                    : editTsByIdData?.description
                }
                onValueChanged={(e) =>
                  handleEditInputChange("description", e.value)
                }
              />
            </div>
          </div>

          <div className="notesFootDrawer">
            <div className="btnSection">
              <div className="cancelSection">
                <Button
                  text="Cancel"
                  height={"40px"}
                  onClick={handleEditCloseSlider}
                />
              </div>

              <div className="create-button">
                <CustomButton
                  text={"Save Details"}
                  onClick={handleEditDetails}
                />
              </div>
            </div>
          </div>
        </div>
      </CustomSlider>
    </>
  );
}
